import {CreateReportModalProps, Step} from "./types";
import Steps from "@components/Steps";
import {FormattedMessage} from "react-intl";
import Modal from "@ui-components/Modal";
import Button from "@ui-components/Button";
import {useContext, useEffect, useMemo, useRef, useState} from "react";
import CountryConfigContext from "../../../../CountryConfigContext";
import {SurveysSelectionEditor} from "./surveys-selection-editor";
import {ContentsEditor} from "./contents-editor";
import {ImagesEditor} from "./images-editor";
import {useDownloadPdf} from "../hooks/useDownloadPdf";
import {ServicesWithIdSchema} from "../../../types";
import _ from "lodash";
import {serviceClassToType} from "@feature/commons/lib/utils";
import {ServicesType} from "@feature/commons/types";
import {SurveysTypeSelector} from "@feature/worksites/detail/create-report-modal/surveys-type-selector";
import {getRemoteKColorResults} from "@feature/remote-calculation-results";
import useCountryAxiosInstance from "@hooks/useCountryAxiosInstance";


const DOUBLE_PRICES_CACHE: Record<number, boolean> = {};


export const CreateReportModal = ({
  worksite,
  onClose,
  servicesSurveys,
  creators,
  images,
  chosenSurveys = [] ,
  openInNewTab = false,
  onReportCreated = undefined,
}: CreateReportModalProps) => {

  const chosenSurveysEntities = servicesSurveys.filter(
    ({id}) => chosenSurveys.includes(id)
  );
  if (
    chosenSurveys?.length > 0
    && _.uniq(chosenSurveysEntities.map(({services: s}) => s!.class)).length > 1
  )
    throw new Error("Invalid surveys pre-selection: all surveys must be of the same type");

  // context
  const {
    config: { services },
  } = useContext(CountryConfigContext);

  // Skip the type selection step if surveys are pre-selected
  const STEPS: Step[] = chosenSurveys.length > 0 ? ["configurations", "contents", "images"] : ["type", "configurations", "contents", "images"];

  // state
  const [currentStep, setCurrentStep] = useState<number>(STEPS.indexOf(chosenSurveys.length > 0 ? "contents" : "type"));
  const [selectedServicesType, setSelectedServicesType] = useState<ServicesType>(
    chosenSurveysEntities.length > 0
      ? serviceClassToType(chosenSurveysEntities[0].services!.class)
      : "quotation"
  );
  const [checkedSurveys, setCheckedSurveys] = useState<Set<number>>(new Set(chosenSurveys || []));
  const [title, setTitle] = useState<string | null>(null);
  const [description, setDescription] = useState<string | null>(null);
  const [includeNotes, setIncludeNotes] = useState<boolean>(false);
  const [includedImages, setIncludedImages]
    = useState<Set<string>>(new Set(images?.map((i) => i.image_url) || []));
  const [coverImage, setCoverImage] = useState<string | null>(null);
  const [hasDoublePrices, setHasDoublePrices] = useState<boolean>(false);
  const [targetPrice, setTargetPrice] = useState<"kerakoll" | "tintometer">("kerakoll");

  // hooks
  const apiRequest = useCountryAxiosInstance();
  const {fetch, download, open, loading, setLoading} = useDownloadPdf();

  // utilities
  const lastSelectedServiceType = useRef<ServicesType>();
  const availableServices = services.filter(
    (s: ServicesWithIdSchema) => serviceClassToType(s.class) === selectedServicesType
  );
  const generatePdfAndExit = async () => {
    // 1. Define the params to be passed to the report generator function
    const methodArgs = [
      selectedServicesType,
      worksite,
      Object.values(creators),
      servicesSurveys.filter(({id}) => checkedSurveys.has(id)),
      title,
      description,
      includeNotes,
      Array.from(includedImages),
      coverImage,
      targetPrice,
    ] as const;
    // 2. Define the method to be used (standard download, open in new tab, or custom report creation)
    const method = !!onReportCreated
      ? (...args: typeof methodArgs) => fetch(...args).then((blob) => {
        if (!blob) return;
        setLoading(true);
        onReportCreated(blob).finally(() => setLoading(false));
      })
      : (openInNewTab ? open : download);
    // 3. Call the method with the params
    await method(...methodArgs);
    // 4. Close the modal
    // onClose();
  };

  const renderContent = () => {
    switch (currentStep) {
      case STEPS.indexOf("type"):  // ----- Surveys type selection
        return <SurveysTypeSelector {...{selectedServicesType, setSelectedServicesType}} />
      case STEPS.indexOf("configurations"):  // ----- Surveys selection
        return <SurveysSelectionEditor {...{
          services: availableServices,
          servicesSurveys: servicesSurveys
            .filter((ss) => availableServices.some(
              (s: ServicesWithIdSchema) => s.id === ss.service_id)
            ),
          checkedSurveys,
          setCheckedSurveys,
          creators,
        }} />
      case STEPS.indexOf("contents"): // ----- Report settings
        return <ContentsEditor {...{
          title,
          setTitle,
          description,
          setDescription,
          includeNotes,
          setIncludeNotes,
        }} />
      case STEPS.indexOf("images"): // ----- Images selection
        return <ImagesEditor {...{
          images,
          includedImages,
          setIncludedImages,
          coverImage,
          setCoverImage,
          hasDoublePrices,
          targetPrice,
          setTargetPrice,
        }}/>
      default:
        throw new Error("Invalid step");
    }
  }

  const getActions = () => {

    const isLastStep = currentStep === STEPS.length - 1;
    const isNextStepDisabled = currentStep === 1 && !checkedSurveys.size;

    const components = [];
    if (currentStep > 0)
      components.push(
        <Button
          full
          styleType="empty"
          onClick={() => { setCurrentStep((prev) => prev - 1)}}
        >
          <FormattedMessage id={"back"} />
        </Button>
      )
    components.push(
      <Button
        full
        onClick={async () => {
          if (isLastStep) await generatePdfAndExit();
          else setCurrentStep((prev) => prev + 1);
        }}
        disabled={isNextStepDisabled}
        submitting={loading}
      >
        <FormattedMessage id={isLastStep ? "download" : "next"} />
      </Button>
    )
    return components;
  }

  useEffect(() => {
    // Empty the checked surveys when the selected services type changes
    if (!lastSelectedServiceType.current) {
      lastSelectedServiceType.current = selectedServicesType;
    } else if (lastSelectedServiceType.current !== selectedServicesType) {
      setCheckedSurveys(new Set());
      lastSelectedServiceType.current = selectedServicesType;
    }
  }, [selectedServicesType, setCheckedSurveys]);


  useEffect(() => {
    // Whenever the checked surveys change, check if any of them has double prices. If so, enable the relevant warning
    // in the last step of the wizard
    const checkDoublePrices = async () => {
      const results = await Promise.all(
        Array.from(checkedSurveys).map(async (ssid) => {
          if (ssid in DOUBLE_PRICES_CACHE)
            return DOUBLE_PRICES_CACHE[ssid];
          const kcr = await getRemoteKColorResults(ssid, apiRequest!) as Record<string, any>;
          const outcome = Object.values(kcr).some((v) => !!v.price?.tintometer);
          DOUBLE_PRICES_CACHE[ssid] = outcome;
          return outcome;
        })
      );
      setHasDoublePrices(results.some(Boolean));
    };
    if (checkedSurveys.size > 0) {
      checkDoublePrices().then();
    } else {
      setHasDoublePrices(false);
    }
  }, [checkedSurveys, apiRequest]);

  return (
    // @ts-ignore
    <Modal onExit={onClose}>
      <h2 className="text-2xl">
        <FormattedMessage id={"pdf_report"} />
      </h2>
      <div className="flex justify-center mt-6 mb-10">
        <Steps current={currentStep} steps={STEPS} />
      </div>
      <div className="my-8">
        {renderContent()}
      </div>
      <div className="flex flex-row gap-3">
        {
          getActions()
            .map((x, idx) => (
              <div key={idx} className="flex-1">{x}</div>
            ))
        }
      </div>
    </Modal>
  );

}